/**
 * TriggerFormFields — input fields for the trigger create/alter form.
 * Extracted from `TriggerEditFormBody` to keep the orchestrator under the
 * 200-line CLAUDE.md budget.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI.
 */
import type { Dispatch, SetStateAction } from 'react';

import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { TriggerEvent, TriggerTiming } from '../../../../sql-resolver/types/ddlEdit';
import {
  EVENT_OPTIONS,
  GRANULARITY_OPTIONS,
  TIMING_OPTIONS,
  toggleEvent,
  type TriggerEditMode,
  type TriggerFormValues,
} from './triggerFormHelpers';

/**
 * Radix Select reserves `''`. `GRANULARITY_OPTIONS` carries a `''`
 * "— default —" entry that `buildTriggerShellDef` reads as "omit the FOR EACH
 * clause", so it travels through the Select as this sentinel and is mapped
 * back to `''` on the way out.
 */
const GRANULARITY_UNSET = '__unset__' as const;

export interface TriggerFormFieldsProps {
  readonly engine: SqlEngine;
  readonly mode: TriggerEditMode;
  readonly values: TriggerFormValues;
  readonly setValues: Dispatch<SetStateAction<TriggerFormValues>>;
  readonly testId: string;
  readonly canBefore: boolean;
  readonly canInsteadOf: boolean;
  readonly multiEventSupported: boolean;
}

export function TriggerFormFields(props: TriggerFormFieldsProps) {
  const { engine, mode, values, setValues, testId, canBefore, canInsteadOf, multiEventSupported } = props;

  const update = <K extends keyof TriggerFormValues>(
    key: K, value: TriggerFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  const onTimingChange = (next: TriggerTiming) => update('timing', next);
  const onEventToggle = (event: TriggerEvent) => {
    if (multiEventSupported) {
      update('events', toggleEvent(values.events, event));
      return;
    }
    update('events', [event]);
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${testId}-name`} className="text-[11px]">Trigger name</Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          className="h-7 text-xs"
          value={values.name}
          onChange={(e) => update('name', e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${testId}-target`} className="text-[11px]">
          Target table (or view){engine === 'oracle' && mode === 'alter' ? ' — ignored on Oracle' : ''}
        </Label>
        <Input
          id={`${testId}-target`}
          data-testid={`${testId}-target`}
          className="h-7 text-xs"
          value={values.target}
          onChange={(e) => update('target', e.target.value)}
          disabled={engine === 'oracle' && mode === 'alter'}
        />
      </div>

      {mode === 'create' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px]">Timing</Label>
            <Select
              value={values.timing}
              onValueChange={(v) => onTimingChange(v as TriggerTiming)}
            >
              <SelectTrigger className="h-7 py-0 text-xs" data-testid={`${testId}-timing`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMING_OPTIONS.filter((o) => {
                  if (o.value === 'before') return canBefore;
                  if (o.value === 'instead-of') return canInsteadOf;
                  return true;
                }).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px]">
              Events {multiEventSupported ? '(multiple allowed)' : '(single event only)'}
            </Label>
            {multiEventSupported ? (
              <div className="flex flex-wrap gap-2" data-testid={`${testId}-events`}>
                {EVENT_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-1 text-[11px]">
                    <Checkbox
                      data-testid={`${testId}-event-${o.value}`}
                      checked={values.events.includes(o.value)}
                      onCheckedChange={() => onEventToggle(o.value)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            ) : (
              <RadioGroup
                className="flex flex-wrap gap-2"
                data-testid={`${testId}-events`}
                value={values.events[0] ?? ''}
                onValueChange={(v) => onEventToggle(v as TriggerEvent)}
              >
                {EVENT_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-1 text-[11px]">
                    <RadioGroupItem value={o.value} data-testid={`${testId}-event-${o.value}`} />
                    {o.label}
                  </label>
                ))}
              </RadioGroup>
            )}
          </div>

          {(engine === 'postgres' || engine === 'oracle' || engine === 'sqlite') ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px]">Granularity</Label>
              <Select
                value={values.granularity === '' ? GRANULARITY_UNSET : values.granularity}
                onValueChange={(v) => update(
                  'granularity',
                  (v === GRANULARITY_UNSET ? '' : v) as TriggerFormValues['granularity'],
                )}
              >
                <SelectTrigger className="h-7 py-0 text-xs" data-testid={`${testId}-granularity`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRANULARITY_OPTIONS.filter((o) => o.value !== 'statement' || engine === 'postgres').map((o) => (
                    <SelectItem key={o.value} value={o.value || GRANULARITY_UNSET}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {engine === 'postgres' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${testId}-execfn`} className="text-[11px]">
                EXECUTE FUNCTION (e.g. <code>audit_change()</code>)
              </Label>
              <Input
                id={`${testId}-execfn`}
                data-testid={`${testId}-execfn`}
                className="h-7 text-xs font-mono"
                value={values.pgsqlExecuteFunction}
                onChange={(e) => update('pgsqlExecuteFunction', e.target.value)}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${testId}-body`} className="text-[11px]">
                Trigger body (opaque text — switch to "Edit as SQL" for the procedural grammar)
              </Label>
              <textarea
                id={`${testId}-body`}
                data-testid={`${testId}-body`}
                className="min-h-[80px] rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
                value={values.bodyText}
                onChange={(e) => update('bodyText', e.target.value)}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            {engine === 'mssql' ? (
              <label className="flex items-center gap-1">
                <Checkbox
                  data-testid={`${testId}-or-alter`}
                  checked={values.mssqlOrAlter}
                  onCheckedChange={(checked) => update('mssqlOrAlter', checked === true)}
                />
                OR ALTER
              </label>
            ) : null}
            {engine === 'postgres' || engine === 'oracle' ? (
              <label className="flex items-center gap-1">
                <Checkbox
                  data-testid={`${testId}-or-replace`}
                  checked={values.orReplace}
                  onCheckedChange={(checked) => update('orReplace', checked === true)}
                />
                OR REPLACE
              </label>
            ) : null}
            {engine === 'oracle' ? (
              <label className="flex items-center gap-1">
                <Checkbox
                  data-testid={`${testId}-editionable`}
                  checked={values.oracleEditionable}
                  onCheckedChange={(checked) => update('oracleEditionable', checked === true)}
                />
                EDITIONABLE
              </label>
            ) : null}
            {engine === 'sqlite' ? (
              <label className="flex items-center gap-1">
                <Checkbox
                  data-testid={`${testId}-temp`}
                  checked={values.sqliteTemporary}
                  onCheckedChange={(checked) => update('sqliteTemporary', checked === true)}
                />
                TEMP
              </label>
            ) : null}
          </div>
        </>
      ) : (
        <RadioGroup
          className="flex items-center gap-3 text-[11px]"
          value={values.enabled ? 'enable' : 'disable'}
          onValueChange={(v) => update('enabled', v === 'enable')}
        >
          <label className="flex items-center gap-1">
            <RadioGroupItem value="enable" data-testid={`${testId}-state-enable`} />
            ENABLE
          </label>
          <label className="flex items-center gap-1">
            <RadioGroupItem value="disable" data-testid={`${testId}-state-disable`} />
            DISABLE
          </label>
        </RadioGroup>
      )}
    </>
  );
}
